import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import {
  useGetOrganizationQuery,
  useUpdateOrganizationMutation,
} from '../../api/organizationApi';
import { useToast } from '../../context/ToastContext';
import { errMsg } from '../../utils/errMsg';
import EntityFormPage from '../../components/EntityFormPage/EntityFormPage';
import buttons from '../../styles/shared/buttons.module.css';

export default function OrganizationEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showToast = useToast();

  const { data: organization, isLoading, isError, error } = useGetOrganizationQuery(id);
  const [updateOrganization, { isLoading: isSaving }] = useUpdateOrganizationMutation();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { name: '', slug: '' },
  });

  useEffect(() => {
    if (organization) reset({ name: organization.name, slug: organization.slug });
  }, [organization, reset]);

  useEffect(() => {
    if (isError) showToast('error', 'Organizations', errMsg(error, 'Could not load organization.'));
  }, [isError, error, showToast]);

  const onSave = async (data) => {
    try {
      await updateOrganization({
        id,
        name: data.name.trim(),
        slug: data.slug.trim(),
      }).unwrap();
      showToast('success', 'Organizations', 'Organization updated.');
      navigate('/admin/organizations');
    } catch (err) {
      showToast(
        'error',
        'Organizations',
        err?.status === 409
          ? 'An organization with that slug already exists.'
          : errMsg(err, 'Could not save organization.'),
      );
    }
  };

  if (isLoading) return <p className="textMuted">Loading…</p>;
  if (!organization) return null;

  return (
    <EntityFormPage title="Edit organization" backTo="/admin/organizations">
      <form onSubmit={handleSubmit(onSave)}>
        <Controller
          name="name"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Name</label>
              <InputText id={field.name} className="w-full mb-3" {...field} />
            </div>
          )}
        />
        <Controller
          name="slug"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div className="field">
              <label htmlFor={field.name}>Slug (subdomain)</label>
              <InputText id={field.name} className="w-full mb-3" {...field} />
            </div>
          )}
        />
        <Button type="submit" label="Save" className={buttons.saveBtn} loading={isSaving} />
      </form>
    </EntityFormPage>
  );
}
